import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import ModalComponent from 'components/common/ModalComponent'

export default function PrintStickerModal ({ isOpen, onClose, sticker, onPrint }) {
  const { t } = useTranslation()
  const [mode, setMode] = useState('all')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const physicalTotal = sticker?.physicalCount ?? 0

  const fromNum = parseInt(from, 10)
  const toNum = parseInt(to, 10)

  const rangeValid = mode === 'all' || (
    from !== '' && to !== '' &&
    Number.isInteger(fromNum) && Number.isInteger(toNum) &&
    fromNum >= 1 && toNum >= fromNum && toNum <= physicalTotal
  )

  const printCount = mode === 'all' ? physicalTotal : (rangeValid ? toNum - fromNum + 1 : 0)

  const handlePrint = async () => {
    if (!rangeValid || isSubmitting) return
    setIsSubmitting(true)
    try {
      await onPrint(mode === 'range' ? { from: fromNum, to: toNum } : null)
    } finally {
      setIsSubmitting(false)
    }
  }

  const rangeError = from && to && !rangeValid
    ? (fromNum > toNum ? t('mgmt.printSticker.fromLeqTo') : t('mgmt.printSticker.rangeBetween', { max: physicalTotal }))
    : null

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={isSubmitting ? undefined : onClose}
      title={t('mgmt.printSticker.title')}
      maxWidth='sm'
      hideDefaultFooter
    >
      <div className='flex flex-col gap-4 py-1'>
        <div className='text-sm text-gray-600 border-b border-gray-100 pb-3'>
          <p className='font-semibold text-gray-900'>{sticker?.workerName || '—'}</p>
          <p className='text-gray-500'>
            {[sticker?.containerName, sticker?.stickerType].filter(Boolean).join(' · ')}
            {physicalTotal > 0 && <span className='ml-1'>· {t('mgmt.printSticker.available', { count: physicalTotal })}</span>}
          </p>
        </div>

        <div className='flex flex-col gap-2'>
          <label className='flex items-center gap-2 text-sm cursor-pointer select-none'>
            <input
              type='radio'
              name='print-mode'
              value='all'
              checked={mode === 'all'}
              onChange={() => setMode('all')}
              className='accent-greenPrimary'
            />
            {physicalTotal > 0 ? t('mgmt.printSticker.printAllN', { count: physicalTotal }) : t('mgmt.printSticker.printAll')}
          </label>
          <label className='flex items-center gap-2 text-sm cursor-pointer select-none'>
            <input
              type='radio'
              name='print-mode'
              value='range'
              checked={mode === 'range'}
              onChange={() => setMode('range')}
              className='accent-greenPrimary'
            />
            {t('mgmt.printSticker.printRange')}
          </label>
        </div>

        {mode === 'range' && (
          <div className='flex flex-col gap-2'>
            <div className='flex gap-3'>
              <div className='flex flex-col gap-1 flex-1'>
                <label className='text-xs font-medium text-gray-700'>{t('mgmt.printSticker.from')}</label>
                <input
                  type='number'
                  min={1}
                  max={physicalTotal}
                  value={from}
                  onChange={e => setFrom(e.target.value)}
                  placeholder='1'
                  className='p-2 border rounded-md shadow-sm text-sm w-full'
                />
              </div>
              <div className='flex flex-col gap-1 flex-1'>
                <label className='text-xs font-medium text-gray-700'>{t('mgmt.printSticker.to')}</label>
                <input
                  type='number'
                  min={1}
                  max={physicalTotal}
                  value={to}
                  onChange={e => setTo(e.target.value)}
                  placeholder={String(physicalTotal)}
                  className='p-2 border rounded-md shadow-sm text-sm w-full'
                />
              </div>
            </div>
            {rangeError && (
              <p className='text-xs text-red-500'>{rangeError}</p>
            )}
            {!rangeError && from && to && rangeValid && (
              <p className='text-xs text-gray-500'>{t('mgmt.printSticker.willPrint', { count: printCount })}</p>
            )}
          </div>
        )}

        <div className='flex justify-end gap-3 pt-2 border-t border-gray-200'>
          <button
            type='button'
            onClick={onClose}
            disabled={isSubmitting}
            className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200 transition disabled:opacity-50'
          >
            {t('mgmt.common.cancel')}
          </button>
          <button
            type='button'
            onClick={handlePrint}
            disabled={!rangeValid || isSubmitting}
            className='px-4 py-2 rounded bg-greenPrimary text-white hover:bg-green-700 transition disabled:opacity-50'
          >
            {isSubmitting
              ? t('mgmt.printSticker.preparing')
              : mode === 'range' && rangeValid && from && to
                ? t('mgmt.printSticker.printN', { count: printCount })
                : t('mgmt.printSticker.print')}
          </button>
        </div>
      </div>
    </ModalComponent>
  )
}
